import React from "react";
import styled from "styled-components/macro";
import { Breadcrumb } from "../ProductsLayout/SharedProductsLayout";
import { LinkUnstyled } from "../LinkUnstyled/LinkUnstyled";
import { PageTitle } from "../../layout/portalPageLayout";
import {
  HeaderContainer,
  SubheaderContainer,
  SubheaderInformationContainer,
} from "../Layout/Layout";
import { StatusBadge } from "../Status/Status";
import type { DetailPageHeaderConfig, MetadataItem } from "./types";
import { t } from "i18next";

// Styled Components
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.resolve("color_neutral_white")};
  padding: ${({ theme }) => theme.resolve("spacing_xl")};
  padding-bottom: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  margin-top: ${({ theme }) => theme.resolve("spacing_lg")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_md")};
`;

const ImageContainer = styled.div<{ clickable?: boolean }>`
  min-width: 100px;
  height: 100px;
  margin-right: ${({ theme }) => theme.resolve("spacing_lg")};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.primaryBorder};
  border-radius: ${({ theme }) => theme.resolve("radius_md")};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.resolve("color_bg_surface_subtle")};
  flex-shrink: 0;
  cursor: ${({ clickable }) => (clickable ? "pointer" : "default")};
  position: relative;

  img {
    max-width: 100%;
    max-height: 100%;
    border-radius: ${({ theme }) => theme.resolve("radius_md")};
  }
`;

const HeaderActions = styled.div`
  position: relative;
  justify-self: end;
  display: flex;
  min-width: 100px;
  gap: ${({ theme }) => theme.resolve("spacing_xl")};
  align-items: center;
  justify-content: flex-end;
  button {
    margin-bottom: 0;
  }
  svg {
    width: 20px !important;
    height: 20px !important;
  }
  margin-left: auto;
  flex-shrink: 0;

  & *[data-alignright] {
    margin-left: auto;
  }
`;

const InnerTitleText = styled.span`
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`;

const LabelTag = styled.span`
  color: ${({ theme }) => theme.resolve("color_text_secondary")};
  border: ${({ theme }) => theme.resolve("border_xs")} solid
    ${({ theme }) => theme.resolve("color_border_lighter")};
  border-radius: ${({ theme }) => theme.resolve("radius_xs")};
  padding: ${({ theme }) => theme.resolve("spacing_xxs")}
    ${({ theme }) => theme.resolve("spacing_xs")};
  font-size: ${({ theme }) => theme.resolve("font_size_sm")};
  font-weight: ${({ theme }) => theme.resolve("font_weight_regular")};
  line-height: ${({ theme }) => theme.resolve("line_height_sm")};
  margin-left: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-right: ${({ theme }) => theme.resolve("spacing_xs")};
  margin-bottom: ${({ theme }) => theme.resolve("spacing_xs")};
  display: inline-block;
  vertical-align: middle;
`;

const SubheaderContentText = styled.span`
  color: ${({ theme }) => theme.primaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.xs};
`;

/**
 * Generic detail page header that works for Products, Customers, Suppliers, Distributors, etc.
 *
 * Uses configuration object instead of product/entity-specific props.
 * No conditionals based on entity type - all differences expressed via config.
 */
export function DetailPageHeader({
  config,
}: {
  config: DetailPageHeaderConfig;
}): JSX.Element {
  const renderImage = () => {
    if (!config.image) return null;

    if (config.image.type === "url") {
      return (
        <ImageContainer
          clickable={!!config.image.onClick}
          onClick={config.image.onClick}
        >
          <img src={config.image.url} alt={config.image.alt} />
          {config.image.badge}
        </ImageContainer>
      );
    } else {
      const Icon = config.image.icon;
      return (
        <ImageContainer>
          <Icon
            width={config.image.size || 40}
            height={config.image.size || 40}
          />
        </ImageContainer>
      );
    }
  };

  const renderMetadataItem = (item: MetadataItem) => {
    if (item.visible === false) return null;

    const Icon = item.icon;
    const value = item.link ? (
      <LinkUnstyled to={item.link}>{item.value}</LinkUnstyled>
    ) : (
      item.value
    );

    return (
      <SubheaderContainer key={item.id}>
        {Icon && <Icon width={16} height={16} />}
        <InnerTitleText>{item.label}:</InnerTitleText>
        <SubheaderContentText>{value}</SubheaderContentText>
      </SubheaderContainer>
    );
  };

  return (
    <HeaderWrapper>
      {/* Breadcrumb navigation */}
      <Breadcrumb>
        {config.breadcrumb.map((item, index) => (
          <React.Fragment key={item.label}>
            {item.path ? (
              <LinkUnstyled to={item.path}>{item.label}</LinkUnstyled>
            ) : (
              <div>{item.label}</div>
            )}
            {index < config.breadcrumb.length - 1 && <div>/</div>}
          </React.Fragment>
        ))}
      </Breadcrumb>

      <HeaderRow>
        {/* Image/Icon */}
        {renderImage()}

        {/* Header Content */}
        <HeaderContainer>
          <PageTitle>
            {config.title}
            {config.isParent && <LabelTag>{t("Parent")}</LabelTag>}
            {config.isSubsidiary && <LabelTag>{t("Subsidiary")}</LabelTag>}
          </PageTitle>

          {/* Status and additional badges */}
          {(config.status || config.additionalBadges) && (
            <SubheaderInformationContainer>
              <SubheaderContainer>
                {config.status && (
                  <StatusBadge
                    text={config.status.label}
                    color={config.status.color as any}
                  />
                )}
                {config.additionalBadges?.map((badge, i) => (
                  <React.Fragment key={i}>{badge}</React.Fragment>
                ))}
              </SubheaderContainer>
            </SubheaderInformationContainer>
          )}

          {/* Metadata rows */}
          {config.metadata.map((row, rowIndex) => (
            <SubheaderInformationContainer key={rowIndex}>
              {row.map(renderMetadataItem)}
            </SubheaderInformationContainer>
          ))}
        </HeaderContainer>

        {/* Actions (status buttons, etc.) */}
        <HeaderActions>{config.actions}</HeaderActions>
      </HeaderRow>
    </HeaderWrapper>
  );
}
